import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  TextField,
  TablePagination,
  Card,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ArgonBox from 'components/ArgonBox';
import ArgonSelect from 'components/ArgonSelect';
import { OrgContext } from 'context/orgContext';
import { useNotifications } from 'context/NotificationContext';
import { getRCCustomObjects, fetchRCCustomObjects, deleteRCCustomObjectVersion } from 'services/customObject';
import CustomObjectExpandableTable from './CustomObjectExpandableTable';
import DeployView from './DeployView';
import GlobalConfigEditor from 'components/GlobalConfigEditor';
import { getConfig, saveConfigDetails } from 'services/org';

function RCCustomObjectsView() {
  const { orgs } = useContext(OrgContext);
  const { addNotification } = useNotifications();

  // State Management
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgOptions, setOrgOptions] = useState([]);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeployView, setShowDeployView] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState(null);
  const [showConfigEditor, setShowConfigEditor] = useState(false);
  const [globalConfig, setGlobalConfig] = useState({ cpq: [], rc: [] });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(6); // Set rows per page to 6

  const [targetOrgForDeploy, setTargetOrgForDeploy] = useState(null);
  const [selectedVersionsForDeploy, setSelectedVersionsForDeploy] = useState({});

  // --- Effects and Data Handling ---

  useEffect(() => {
    if (orgs && orgs.length > 0) {
      const rcOrgs = orgs.filter((org) => org.type === 'RC');
      setOrgOptions(rcOrgs.map((org) => ({ label: org.name, value: org.org_id })));
      if (rcOrgs.length > 0 && !selectedOrg) {
        setSelectedOrg(rcOrgs[0].org_id);
      }
    }
  }, [orgs, selectedOrg]);

  const fetchDataFromDB = async () => {
    if (!selectedOrg) return;
    setIsLoading(true);
    // FIX: Corrected title format
    addNotification({ title: ['Loading Data'], details: 'Fetching custom object data...', severity: 'info' });
    try {
      const response = await getRCCustomObjects(selectedOrg);
      if (response.success) {
        setData(response.data);
      } else {
        throw new Error(response.error || 'Failed to fetch data.');
      }
    } catch (error) {
      // FIX: Corrected title format
      addNotification({ title: ['Error'], details: error.message, severity: 'error' });
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDataFromDB();
  }, [selectedOrg]);

  // --- Search and Pagination Logic ---
  const filteredData = useMemo(() => {
    if (!searchTerm) {
      return data;
    }
    return data.filter((group) => group.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [data, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // --- Handlers ---

  const handleOrgChange = (event) => {
    const newOrgId = event.target.value;
    setSelectedOrg(newOrgId);
    setPage(0);
    setTargetOrgForDeploy(null);
    setSelectedVersionsForDeploy({});
  };

  const handleRefreshFromSalesforce = async () => {
    if (!selectedOrg) return;
    setIsLoading(true);
    // FIX: Corrected syntax and title format
    addNotification({ title: ['Fetching from Salesforce'], details: 'This may take a moment...', severity: 'info' });
    try {
      const response = await fetchRCCustomObjects(selectedOrg);
      // FIX: Corrected title format
      addNotification({ title: ['Fetch Complete'], details: response.message, severity: 'success' });
      await fetchDataFromDB();
    } catch (error) {
      // FIX: Corrected title format
      addNotification({ title: ['Error', 'Fetch Failed'], details: error.message, severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenConfigEditor = async () => {
    setIsLoading(true);
    try {
      const { config } = await getConfig();
      setGlobalConfig({
        cpq: Array.isArray(config.cpq) ? config.cpq : [],
        rc: Array.isArray(config.rc) ? config.rc : [],
      });
      setShowConfigEditor(true);
    } catch (error) {
      // FIX: Corrected title format
      addNotification({
        title: ['Error'],
        details: `Could not load configuration: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveConfig = async (updatedConfig) => {
    setIsLoading(true);
    try {
      await saveConfigDetails(updatedConfig);
      // FIX: Corrected title format
      addNotification({ title: ['Success'], details: 'Configuration saved successfully.', severity: 'success' });
      setShowConfigEditor(false);
    } catch (error) {
      // FIX: Corrected title format
      addNotification({
        title: ['Error'],
        details: `Could not save configuration: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (version) => {
    setVersionToDelete(version);
    setDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!versionToDelete) return;
    setIsLoading(true);
    try {
      const response = await deleteRCCustomObjectVersion(versionToDelete._id);
      // FIX: Corrected title format
      addNotification({ title: ['Success'], details: response.message, severity: 'success' });
      await fetchDataFromDB();
    } catch (error) {
      // FIX: Corrected title format
      addNotification({ title: ['Error'], details: error.message, severity: 'error' });
    } finally {
      setIsLoading(false);
      setDialogOpen(false);
      setVersionToDelete(null);
    }
  };

  const handleGoToDeploy = () => {
    const initialSelections = {};
    data.forEach((group) => {
      const latestVersion = group.versions.find((v) => v.is_latest);
      if (latestVersion) {
        initialSelections[latestVersion._id] = true;
      }
    });
    setSelectedVersionsForDeploy(initialSelections);
    setShowDeployView(true);
  };

  const handleBackFromDeploy = () => {
    setShowDeployView(false);
  };

  // --- Render Logic ---

  if (showDeployView) {
    return (
      <DeployView
        allData={data}
        sourceOrgId={selectedOrg}
        onBack={handleBackFromDeploy}
        targetOrg={targetOrgForDeploy}
        setTargetOrg={setTargetOrgForDeploy}
        selectedVersions={selectedVersionsForDeploy}
        setSelectedVersions={setSelectedVersionsForDeploy}
      />
    );
  }

  return (
    <Card>
      <ArgonBox p={2}>
        {/* HEADER SECTION */}
        <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
          <ArgonSelect
            placeholder="Select an RC Org"
            options={orgOptions}
            value={selectedOrg || ''}
            onChange={handleOrgChange}
            sx={{ minWidth: '250px' }}
          />
          <Box display="flex" alignItems="center" flexGrow={1} justifyContent="flex-end" gap={1}>
            <TextField
              placeholder="Search Objects..."
              variant="outlined"
              size="small" // Changed back to small for consistency
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              sx={{ width: '300px' }}
            />
            <Tooltip title="Add New Object to Configuration">
              <Button
                variant="contained"
                color="info"
                onClick={handleOpenConfigEditor}
                disabled={isLoading}
                sx={{ minWidth: 'auto', px: 1.5 }}
              >
                <AddCircleOutlineIcon />
              </Button>
            </Tooltip>
            <Tooltip title="Refresh from Salesforce">
              <Button
                variant="contained"
                color="secondary"
                onClick={handleRefreshFromSalesforce}
                disabled={isLoading || !selectedOrg}
                sx={{ minWidth: 'auto', px: 1.5 }}
              >
                <RefreshIcon color="action" />
              </Button>
            </Tooltip>
          </Box>
        </ArgonBox>

        {/* BODY / TABLE SECTION */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={5}>
            <CircularProgress />
          </Box>
        ) : paginatedData.length > 0 ? (
          <CustomObjectExpandableTable data={paginatedData} onDelete={handleDeleteClick} />
        ) : (
          <Typography sx={{ textAlign: 'center', p: 3 }}>
            {searchTerm ? 'No objects match your search.' : 'No custom object data found for this organization.'}
          </Typography>
        )}

        {/* FOOTER / PAGINATION & DEPLOY BUTTON SECTION */}
        <ArgonBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
          <ArgonBox />

          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[]}
            labelRowsPerPage=""
          />
          <Button
            variant="contained"
            color="info"
            startIcon={<RocketLaunchIcon />}
            onClick={handleGoToDeploy}
            disabled={isLoading || !selectedOrg || data.length === 0}
          >
            Deploy
          </Button>
        </ArgonBox>
      </ArgonBox>

      {/* MODALS */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete version <strong>{versionToDelete?.version}</strong> of{' '}
            <strong>{versionToDelete?.name}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="primary" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {showConfigEditor && (
        <GlobalConfigEditor
          open={showConfigEditor}
          onClose={() => setShowConfigEditor(false)}
          initialConfig={globalConfig}
          onSave={handleSaveConfig}
          isLoading={isLoading}
        />
      )}
    </Card>
  );
}

export default RCCustomObjectsView;
